/**
 * DailyTimeline - Vista de protocolos diarios como timeline
 *
 * Muestra los protocolos del día en una lista simple con:
 * - Hora ideal prominente a la izquierda
 * - Checkboxes de subtareas visibles
 * - Agrupación visual por bloque horario
 * - Grupos colapsables por bloque (mañana, tarde, noche)
 */

import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { Sunrise, Sun, Moon, Clock, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn, formatMinutes } from '@/core/utils';
import { getTimeBlockColors } from '@/core/constants';
import { useTimeBlocks } from '@/core/hooks';
import type { TimeBlockGroup, TimeBlock, Protocol } from '@/core/types';
import { Badge } from '@/ui/primitives';
import { useQuickTaskStore } from '@/store';
import { TimelineProtocolCard } from './TimelineProtocolCard';
import { QuickTaskItem } from './QuickTaskItem';

const timeBlockIcons: Record<TimeBlock, LucideIcon> = {
  morning: Sunrise,
  afternoon: Sun,
  evening: Moon,
  anytime: Clock,
};

interface DailyTimelineProps {
  groups: TimeBlockGroup[];
  currentTimeBlock: TimeBlock;
}

export function DailyTimeline({ groups, currentTimeBlock }: DailyTimelineProps) {
  const timeBlocks = useTimeBlocks();
  const quickTasks = useQuickTaskStore((state) => state.quickTasks);
  const cleanupOldTasks = useQuickTaskStore((state) => state.cleanupOldTasks);

  // Limpiar tareas de días anteriores al montar
  useEffect(() => {
    cleanupOldTasks();
  }, [cleanupOldTasks]);

  // Obtener tareas rápidas de hoy por bloque
  const getQuickTasksForBlock = (block: TimeBlock) => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    return quickTasks.filter((t) => t.timeBlock === block && t.createdDate === today);
  };

  // Estado para controlar qué bloques están expandidos (por defecto todos expandidos)
  const [expandedBlocks, setExpandedBlocks] = useState<Record<TimeBlock, boolean>>({
    morning: true,
    afternoon: true,
    evening: true,
    anytime: true,
  });

  const toggleBlock = (block: TimeBlock) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [block]: !prev[block],
    }));
  };

  return (
    <div className="space-y-6">
      {groups.map((group) => {
        const config = timeBlocks[group.timeBlock];
        const colors = getTimeBlockColors(group.timeBlock);
        const Icon = timeBlockIcons[group.timeBlock];
        const isCurrentBlock = group.timeBlock === currentTimeBlock;
        const isComplete = group.isFullyCompleted;
        const isExpanded = expandedBlocks[group.timeBlock];

        return (
          <section
            key={group.timeBlock}
            className={cn(
              'relative rounded-xl border overflow-hidden transition-all duration-300',
              isCurrentBlock && !isComplete
                ? cn('border-2', colors.border, 'shadow-lg')
                : isComplete
                ? 'border-emerald-800/50'
                : 'border-slate-800'
            )}
          >
            {/* Header del bloque horario - clickeable para colapsar/expandir */}
            <button
              onClick={() => toggleBlock(group.timeBlock)}
              className={cn(
                'w-full flex items-center gap-3 p-4 transition-colors',
                isComplete
                  ? 'bg-emerald-900/20 hover:bg-emerald-900/30'
                  : cn(colors.bgTransparent, 'hover:bg-opacity-70')
              )}
            >
              <div
                className={cn(
                  'p-2 rounded-lg',
                  isComplete ? 'bg-emerald-900/30' : colors.bgTransparent
                )}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <Icon className={cn('w-5 h-5', colors.text)} />
                )}
              </div>

              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <h2
                    className={cn(
                      'font-semibold text-lg',
                      isComplete ? 'text-emerald-400' : colors.text
                    )}
                  >
                    {config.label}
                  </h2>
                  {isCurrentBlock && !isComplete && (
                    <Badge
                      variant="outline"
                      size="xs"
                      className="border-current animate-pulse"
                    >
                      Ahora
                    </Badge>
                  )}
                  <Badge
                    variant={isComplete ? 'subtle' : 'default'}
                    size="xs"
                    className={isComplete ? 'bg-emerald-900/30 text-emerald-400' : ''}
                  >
                    {group.completedCount}/{group.totalCount}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">{config.timeRange}</p>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      isComplete ? 'text-emerald-400' : colors.text
                    )}
                  >
                    {Math.round((group.completedCount / Math.max(group.totalCount, 1)) * 100)}%
                  </p>
                  <p className="text-xs text-slate-500">
                    ~{formatMinutes(group.totalTime)}
                  </p>
                </div>

                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-slate-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-500" />
                )}
              </div>
            </button>

            {/* Timeline de protocolos - animado */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="relative pl-4 border-l-2 border-slate-800 space-y-4 ml-8 mr-4 pt-4 pb-4">
                    {group.protocols.map((protocol: Protocol, index: number) => (
                      <motion.div
                        key={protocol.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="relative"
                      >
                        {/* Punto en la línea del tiempo */}
                        <div
                          className={cn(
                            'absolute -left-[21px] top-3 w-3 h-3 rounded-full border-2',
                            protocol.isCompleted
                              ? 'bg-emerald-500 border-emerald-500'
                              : 'bg-slate-900 border-slate-600'
                          )}
                        />
                        <TimelineProtocolCard protocol={protocol} />
                      </motion.div>
                    ))}

                    {/* Tareas rápidas del bloque */}
                    {(() => {
                      const blockQuickTasks = getQuickTasksForBlock(group.timeBlock);
                      return blockQuickTasks.length > 0 && (
                        <div className="space-y-2 pt-2 border-t border-dashed border-slate-800">
                          <AnimatePresence>
                            {blockQuickTasks.map((task, index) => (
                              <QuickTaskItem key={task.id} task={task} index={index} />
                            ))}
                          </AnimatePresence>
                        </div>
                      );
                    })()}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        );
      })}
    </div>
  );
}
